import React, { useEffect, useRef, useState } from "react";
import { Chart } from "../../engine";
import { GoToLiveIcon } from "../../design/icons/ChartIconRegistry";

// Thin React host for the Fela R.I.O Chart Engine. All rendering/interaction lives in /engine.
export default function ChartCanvas({
  candles, chartType, overlays, panels, showVolume, structure, structureFlags,
  drawings, activeTool, toolMeta, futureFraction, snap, symbol, selectedId, theme, decimals, tfSeconds, scaleMode,
  onHover, onAddDrawing, onDrawingsChange, onSelect, onEditText, onContextMenu, onChartReady,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const cbRef = useRef({});
  const [live, setLive] = useState(true);
  cbRef.current = { onHover, onAddDrawing, onDrawingsChange, onSelect, onEditText, onContextMenu, onChartReady };

  useEffect(() => {
    const chart = new Chart(canvasRef.current);
    chart.onHover((c) => cbRef.current.onHover?.(c));
    chart.onDrawingComplete((d) => cbRef.current.onAddDrawing?.(d));
    chart.onDrawingsChange((list) => cbRef.current.onDrawingsChange?.(list));
    chart.onSelect((id) => cbRef.current.onSelect?.(id));
    chart.onEditText((id) => cbRef.current.onEditText?.(id));
    chart.onViewChange((s) => setLive(s.isLiveFollowing));
    chartRef.current = chart;
    cbRef.current.onChartReady?.(chart);
    const canvas = canvasRef.current;
    const onCtx = (e) => {
      e.preventDefault();
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      const pt = chart.pointAt(x, y);
      if (!pt) return;
      const f = chart.currentFrame;
      const drawingId = f ? chart.drawings.hitAt(x, y, f) : null;
      if (drawingId) chart.selectDrawing(drawingId);
      cbRef.current.onContextMenu?.({ x, y, maxX: r.width, price: pt.price, time: f ? f.timeAt(x) : null, candle: chart.candles[pt.index] ?? null, drawingId });
    };
    canvas.addEventListener("contextmenu", onCtx);
    return () => { canvas.removeEventListener("contextmenu", onCtx); chart.destroy(); chartRef.current = null; cbRef.current.onChartReady?.(null); };
  }, []);

  useEffect(() => { chartRef.current?.setData(candles); }, [candles]);
  useEffect(() => {
    chartRef.current?.setOptions({ chartType, overlays, panels, showVolume, structure, structureFlags, drawings, snap, theme, decimals, tfSeconds, scaleMode });
  }, [chartType, overlays, panels, showVolume, structure, structureFlags, drawings, snap, theme, decimals, tfSeconds, scaleMode]);
  useEffect(() => { chartRef.current?.setSymbol(symbol); }, [symbol]);
  useEffect(() => { const c = chartRef.current; if (c && futureFraction != null) { c.viewport.futureFraction = futureFraction; if (c.viewport.isLiveFollowing) c.viewport.goLive(c.candles.length); c.requestRender(); } }, [futureFraction]);
  useEffect(() => { chartRef.current?.setTool(activeTool, toolMeta); }, [activeTool, toolMeta]);
  useEffect(() => { chartRef.current?.selectDrawing(selectedId); }, [selectedId]);

  const L = chartRef.current?.layout;
  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="chart-canvas"
        className="w-full h-full block"
        style={{ cursor: activeTool === "select" ? "default" : "crosshair", touchAction: "none" }}
      />
      {!live && (
        <button
          data-testid="go-to-live-btn"
          title="Go to live price · reset auto scale"
          onClick={() => chartRef.current?.goToLive()}
          className="absolute z-20 h-8 w-8 rounded-full border border-primary/50 bg-panel/90 backdrop-blur text-primary shadow-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors animate-in fade-in"
          style={{ right: (L?.rightAxis ?? 66) + 12, bottom: (L ? L.h - L.priceBottom : 22) + 12 }}
        >
          <GoToLiveIcon size={16} weight="bold" />
        </button>
      )}
    </>
  );
}
